import ContentCutIcon from "@mui/icons-material/ContentCut";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import {
	Box,
	Button,
	Card,
	CardContent,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	DataGrid,
	GridActionsCellItem,
	type GridColDef,
	type GridEventListener,
	GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { useState } from "react";
import {
	AMOUNT_WARNING_THRESHOLD_CENTS,
	MAX_RECENT_EVENTS_IN_SPLIT_DIALOG,
	PAYPAL_CSV_COLUMNS,
} from "@/configConstants";
import { createFrontendTrpcClient, formatCurrency } from "../../utils";
import { parseEventDate, toBerlinDateTimeFromISO } from "../../utils/dates";
import { calculateCostPerParticipant } from "./matching";
import {
	convertPayPalDatetimeToISO,
	formatEventDisplayName,
	formatUserDisplayName,
	getValue,
	type PaymentRow,
	type PayPalPayment,
} from "./payment-import-logic";
import SplitPaymentDialog from "./SplitPaymentDialog";

const trpcClient = createFrontendTrpcClient();

type PaymentDataGridProps = {
	paymentRows: PaymentRow[];
	paypalPayments: Map<string, PayPalPayment>;
	importData: Awaited<
		ReturnType<typeof trpcClient.payment.getPaymentImportData.query>
	>;
	onRemoveRow: (paymentId: string) => void;
	onSplitPayment: (paymentId: string, amounts: number[]) => void;
	onUpdateRow: (paymentId: string, updates: Partial<PaymentRow>) => void;
	onToggleCheck: (paymentId: string, checked: boolean) => void;
	onToggleAllChecked: (checked: boolean) => void;
};

export default function PaymentDataGrid({
	paymentRows,
	paypalPayments,
	importData,
	onRemoveRow,
	onSplitPayment,
	onUpdateRow,
	onToggleCheck,
	onToggleAllChecked,
}: PaymentDataGridProps) {
	const getGridRows = () =>
		paymentRows.map((row) => {
			const paypalPayment = paypalPayments.get(row.paypalTransactionCode);
			if (!paypalPayment) {
				return {
					id: row.id,
					date: toBerlinDateTimeFromISO(new Date().toISOString()).toJSDate(),
					hinweis: undefined,
					userId: row.userId ?? "",
					eventId: row.eventId ?? "",
					amount: row.amountCents,
					paymentId: row.id,
					transactionCode: row.paypalTransactionCode,
					checked: row.checked ?? true,
				};
			}

			const dateStr = String(
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.DATE) ?? "",
			);
			const timeStr = String(
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.TIME) ?? "",
			);
			const tzStr = String(
				getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.TIMEZONE) ?? "",
			);
			const iso = convertPayPalDatetimeToISO(dateStr, timeStr, tzStr);
			const dateObj = toBerlinDateTimeFromISO(iso).toJSDate();
			return {
				id: row.id,
				date: dateObj,
				from: `${getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.NAME)}`,
				fromEmail: getValue(
					paypalPayment.raw,
					PAYPAL_CSV_COLUMNS.PAYER_EMAIL,
				) as string | undefined,
				fromPhone: getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.PHONE) as
					| string
					| undefined,
				hinweis: getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.NOTE) as
					| string
					| undefined,
				userId: row.userId ?? "",
				eventId: row.eventId ?? "",
				amount: row.amountCents,
				paymentId: row.id,
				transactionCode: row.paypalTransactionCode,
				checked: row.checked ?? true,
			};
		});

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{
		id: string;
		amount: number;
		date: Date;
		from: string;
		userId?: string;
		eventId?: number | string;
	} | null>(null);
	const [splitDialogOpen, setSplitDialogOpen] = useState(false);
	const [splitTarget, setSplitTarget] = useState<{
		id: string;
		amount: number;
		paymentInfo?: {
			date: string;
			from: string;
			email?: string;
			phone?: string;
			note?: string;
		};
		relevantEvents?: Array<{
			id: number;
			date: string;
			costPerParticipant: number;
		}>;
	} | null>(null);

	const handleDeleteClick = (paymentId: string) => () => {
		const gridRow = gridRows.find((r) => r.paymentId === paymentId);
		const paymentRow = paymentRows.find((r) => r.id === paymentId);
		if (gridRow && paymentRow) {
			setDeleteTarget({
				id: paymentId,
				amount: gridRow.amount,
				date: gridRow.date,
				from: gridRow.from,
				userId: paymentRow.userId ?? undefined,
				eventId: paymentRow.eventId,
			});
			setDeleteDialogOpen(true);
		}
	};

	const handleSplitClick =
		(paymentId: string, amount: number, transactionCode: string) => () => {
			const paypalPayment = paypalPayments.get(transactionCode);
			if (!paypalPayment) {
				setSplitTarget({ id: paymentId, amount });
				setSplitDialogOpen(true);
				return;
			}

			// Format payment info
			const iso = convertPayPalDatetimeToISO(
				String(getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.DATE) ?? ""),
				String(getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.TIME) ?? "00:00"),
				String(getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.TIMEZONE) ?? ""),
			);
			const dateObj = toBerlinDateTimeFromISO(iso).toJSDate();
			const formatter = new Intl.DateTimeFormat("de-DE", {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
				timeZone: "Europe/Berlin",
			});

			const paymentInfo = {
				date: formatter.format(dateObj),
				from: String(
					getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.NAME) ?? "",
				),
				email: getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.PAYER_EMAIL) as
					| string
					| undefined,
				phone: getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.PHONE) as
					| string
					| undefined,
				note: getValue(paypalPayment.raw, PAYPAL_CSV_COLUMNS.NOTE) as
					| string
					| undefined,
			};

			// Filter events that happened on or before payment date
			const paymentDateString = DateTime.fromJSDate(dateObj)
				.setZone("Europe/Berlin")
				.toISODate();
			const relevantEvents = importData.events
				.filter((event) => {
					return event.date <= paymentDateString;
				})
				.sort((a, b) => b.date.localeCompare(a.date)) // Most recent first
				.slice(0, MAX_RECENT_EVENTS_IN_SPLIT_DIALOG) // Limit to configured number of most recent events
				.map((event) => {
					const costPerParticipant = calculateCostPerParticipant(event) ?? 0;
					const eventDateObj = parseEventDate(event.date).toJSDate();
					const eventFormatter = new Intl.DateTimeFormat("de-DE", {
						day: "2-digit",
						month: "2-digit",
						year: "numeric",
						timeZone: "Europe/Berlin",
					});
					return {
						id: event.id,
						date: eventFormatter.format(eventDateObj),
						costPerParticipant,
					};
				});

			setSplitTarget({
				id: paymentId,
				amount,
				paymentInfo,
				relevantEvents: relevantEvents as {
					id: number;
					date: string;
					costPerParticipant: number;
				}[],
			});
			setSplitDialogOpen(true);
		};

	const handleConfirmSplit = (amounts: number[]) => {
		if (!splitTarget) return;
		onSplitPayment(splitTarget.id, amounts);
		setSplitTarget(null);
		setSplitDialogOpen(false);
	};

	const handleCancelSplit = () => {
		setSplitTarget(null);
		setSplitDialogOpen(false);
	};

	const handleConfirmDelete = () => {
		if (!deleteTarget) return;
		onRemoveRow(deleteTarget.id);
		setDeleteTarget(null);
		setDeleteDialogOpen(false);
	};

	const handleCancelDelete = () => {
		setDeleteTarget(null);
		setDeleteDialogOpen(false);
	};

	const handleRowEditStop: GridEventListener<"rowEditStop"> = (
		params,
		event,
	) => {
		if (params.reason === GridRowEditStopReasons.rowFocusOut) {
			event.defaultMuiPrevented = true;
		}
	};

	const handleProcessRowUpdate = (
		newRow: Record<string, any>,
		oldRow: Record<string, any>,
	) => {
		if (newRow.userId !== oldRow.userId || newRow.eventId !== oldRow.eventId) {
			// Calculate new cost per participant and amount indicator
			let costPerParticipant: number | null = null;
			let amountIndicator: "info" | "warning" | null = null;

			if (newRow.eventId && newRow.eventId !== "") {
				const event = importData.events.find((e) => e.id === newRow.eventId);
				if (event) {
					costPerParticipant = calculateCostPerParticipant(event);

					// Analyze amount
					const amountCents = newRow.amount;
					if (costPerParticipant !== null) {
						if (amountCents < costPerParticipant) {
							amountIndicator = "info";
						} else if (
							amountCents >
							costPerParticipant + AMOUNT_WARNING_THRESHOLD_CENTS
						) {
							amountIndicator = "warning";
						}
					}
				}
			}

			onUpdateRow(newRow.paymentId, {
				userId: newRow.userId,
				eventId: newRow.eventId,
				costPerParticipant,
				amountIndicator,
			});
		}
		return newRow;
	};

	const allChecked = paymentRows.every(
		(row) => row.checked === true || row.checked === undefined,
	);
	const someChecked = paymentRows.some(
		(row) => row.checked === true || row.checked === undefined,
	);

	const columns: GridColDef[] = [
		{
			field: "checked",
			headerName: "",
			width: 50,
			editable: false,
			sortable: false,
			filterable: false,
			type: "boolean",
			disableColumnMenu: true,
			renderHeader: () => (
				<input
					type="checkbox"
					checked={allChecked}
					ref={(input) => {
						if (input) {
							input.indeterminate = !allChecked && someChecked;
						}
					}}
					onChange={(e) => onToggleAllChecked(e.target.checked)}
					style={{ cursor: "pointer" }}
					title={allChecked ? "Alle abwählen" : "Alle auswählen"}
				/>
			),
			renderCell: (params) => (
				<input
					type="checkbox"
					checked={params.value ?? true}
					onChange={(e) =>
						onToggleCheck(params.row.paymentId, e.target.checked)
					}
					style={{ cursor: "pointer" }}
				/>
			),
		},
		{
			field: "date",
			headerName: "Datum",
			width: 140,
			editable: false,
			type: "dateTime",
			renderCell: (params) => {
				const date = params.value as Date;
				if (!date) return "-";
				const formatter = new Intl.DateTimeFormat("de-DE", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
					timeZone: "Europe/Berlin",
				});
				return formatter.format(date);
			},
		},

		{
			field: "from",
			headerName: "Von",
			width: 150,
			editable: false,
			renderCell: (params) => {
				const email = params.row.fromEmail;
				const phone = params.row.fromPhone;
				return (
					<Tooltip title={`${email}${phone ? `, ${phone}` : ""}`}>
						<Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
							{params.value}
						</Box>
					</Tooltip>
				);
			},
		},
		{
			field: "hinweis",
			headerName: "Hinweis",
			width: 200,
			editable: false,
			renderCell: (params) => {
				const hinweis = params.value as string | undefined;
				if (!hinweis) return "-";
				return (
					<Tooltip title={hinweis}>
						<Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
							{hinweis}
						</Box>
					</Tooltip>
				);
			},
		},
		{
			field: "userId",
			headerName: "Benutzer",
			width: 200,
			editable: true,
			type: "singleSelect",
			valueOptions: [
				{ value: "", label: "-" },
				...importData.users
					.map((u) => ({ value: u.id, label: formatUserDisplayName(u) }))
					.sort((a, b) =>
						a.label.localeCompare(b.label, "de-DE", { sensitivity: "base" }),
					),
			],
			renderCell: (params) => {
				const user = importData.users.find((u) => u.id === params.value);
				return user ? formatUserDisplayName(user) : "-";
			},
		},

		{
			field: "eventId",
			headerName: "Veranstaltung",
			width: 240,
			editable: true,
			type: "singleSelect",
			valueOptions: (params) => {
				// Get the payment date for this row
				const paymentDate = params.row?.date as Date | undefined;
				if (!paymentDate) {
					return [{ value: "", label: "-" }];
				}

				// Convert payment date to date-only string (YYYY-MM-DD) for comparison
				const year = paymentDate.getFullYear();
				const month = String(paymentDate.getMonth() + 1).padStart(2, "0");
				const day = String(paymentDate.getDate()).padStart(2, "0");
				const paymentDateStr = `${year}-${month}-${day}`;

				// Get currently selected event ID
				const currentEventId = params.row?.eventId;

				// Filter events that happened on or before the payment date
				const filteredEvents = importData.events.filter((event) => {
					// event.date is already in YYYY-MM-DD format
					return event.date <= paymentDateStr;
				});

				// If current event is selected but not in filtered list, add it
				// (this prevents console warnings about out-of-range values)
				if (currentEventId && currentEventId !== "") {
					const currentEventInList = filteredEvents.some(
						(e) => e.id === currentEventId,
					);
					if (!currentEventInList) {
						const currentEvent = importData.events.find(
							(e) => e.id === currentEventId,
						);
						if (currentEvent) {
							filteredEvents.push(currentEvent);
						}
					}
				}

				return [
					{ value: "", label: "-" },
					...filteredEvents
						.sort((a, b) => b.date.localeCompare(a.date))
						.map((e) => {
							const costPerParticipant = calculateCostPerParticipant(e) ?? 0;
							return {
								value: e.id,
								label: `${formatEventDisplayName(e)} (${formatCurrency(costPerParticipant)})`,
							};
						}),
				];
			},
			renderCell: (params) => {
				if (!params.value) return "-";
				const event = importData.events.find((e) => e.id === params.value);
				if (!event) return "-";
				const costPerParticipant = calculateCostPerParticipant(event) ?? 0;
				return `${formatEventDisplayName(event)} (${formatCurrency(costPerParticipant)})`;
			},
		},
		{
			field: "amount",
			headerName: "Betrag",
			width: 140,
			editable: false,
			renderCell: (params) => {
				const amountCents = params.value;
				const amount = formatCurrency(amountCents);

				// Find the corresponding payment row to get indicator
				const paymentRow = paymentRows.find(
					(row) => row.id === params.row.paymentId,
				);
				const indicator = paymentRow?.amountIndicator;
				const hasUser = paymentRow?.userId && paymentRow.userId !== "";

				// Get cost per participant for tooltip
				let tooltipText = "";
				if (hasUser && paymentRow?.eventId && paymentRow.eventId !== "") {
					const event = importData.events.find(
						(e) => e.id === paymentRow.eventId,
					);
					if (event) {
						const costPerParticipant = calculateCostPerParticipant(event) ?? 0;
						const expectedAmount = formatCurrency(costPerParticipant);

						if (indicator === "info") {
							tooltipText = `Zahlung kleiner als erwartet (${expectedAmount})`;
						} else if (indicator === "warning") {
							tooltipText = `Zahlung größer als erwartet (${expectedAmount})`;
						}
					}
				}

				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<span>{amount}</span>
						{hasUser && indicator === "info" && (
							<Tooltip title={tooltipText}>
								<InfoIcon color="info" fontSize="small" />
							</Tooltip>
						)}
						{hasUser && indicator === "warning" && (
							<Tooltip title={tooltipText}>
								<WarningIcon color="warning" fontSize="small" />
							</Tooltip>
						)}
					</Box>
				);
			},
		},
		{
			field: "actions",
			type: "actions",
			headerName: "Aktionen",
			width: 100,
			getActions: (params) => [
				<GridActionsCellItem
					key="split"
					icon={<ContentCutIcon />}
					label="Aufteilen"
					onClick={handleSplitClick(
						params.row.paymentId,
						params.row.amount,
						params.row.transactionCode,
					)}
				/>,
				<GridActionsCellItem
					key="delete"
					icon={<DeleteIcon />}
					label="Löschen"
					onClick={handleDeleteClick(params.row.paymentId)}
				/>,
			],
		},
	];

	const gridRows = getGridRows();

	return (
		<Card
			sx={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}
		>
			<CardContent
				sx={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
					minHeight: 0,
					p: 2,
				}}
			>
				<Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
					<DataGrid
						rows={gridRows}
						columns={columns}
						editMode="cell"
						onRowEditStop={handleRowEditStop}
						processRowUpdate={handleProcessRowUpdate}
						getRowClassName={(params) => {
							const paymentRow = paymentRows.find(
								(row) => row.id === params.row.paymentId,
							);
							const hasUser = paymentRow?.userId && paymentRow.userId !== "";
							return !hasUser ? "row-no-user" : "";
						}}
						initialState={{
							sorting: {
								sortModel: [
									{
										field: "date",
										sort: "desc",
									},
								],
							},
						}}
						sx={{
							"& .MuiDataGrid-root": {
								border: "none",
							},
							"& .row-no-user": {
								backgroundColor: "rgba(255, 0, 0, 0.08)",
								"&:hover": {
									backgroundColor: "rgba(255, 0, 0, 0.12)",
								},
							},
						}}
					/>
					<Dialog
						open={deleteDialogOpen}
						onClose={handleCancelDelete}
						aria-labelledby="delete-dialog-title"
					>
						<DialogTitle id="delete-dialog-title">Zahlung löschen?</DialogTitle>
						<DialogContent>
							<DialogContentText>
								Möchten Sie diese Zahlung wirklich aus der Importliste
								entfernen? Diese Aktion kann nicht rückgängig gemacht werden.
							</DialogContentText>
							{deleteTarget && (
								<Box
									sx={{
										mt: 2,
										p: 2,
										backgroundColor: "grey.100",
										borderRadius: 1,
									}}
								>
									<Typography variant="body2">
										<strong>Von:</strong> {deleteTarget.from}
									</Typography>
									<Typography variant="body2">
										<strong>Betrag:</strong> €
										{(deleteTarget.amount / 100).toFixed(2)}
									</Typography>
									<Typography variant="body2">
										<strong>Datum:</strong>{" "}
										{new Intl.DateTimeFormat("de-DE", {
											year: "numeric",
											month: "2-digit",
											day: "2-digit",
											timeZone: "Europe/Berlin",
										}).format(deleteTarget.date)}
									</Typography>
									{deleteTarget.userId && (
										<Typography variant="body2">
											<strong>Benutzer:</strong>{" "}
											{formatUserDisplayName(
												importData.users.find(
													(u) => u.id === deleteTarget.userId,
												) || {},
											)}
										</Typography>
									)}
									{deleteTarget.eventId && deleteTarget.eventId !== "" && (
										<Typography variant="body2">
											<strong>Veranstaltung:</strong>{" "}
											{formatEventDisplayName(
												importData.events.find(
													(e) => e.id === deleteTarget.eventId,
												) || { date: "" },
											)}
										</Typography>
									)}
								</Box>
							)}
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCancelDelete}>Abbrechen</Button>
							<Button color="error" onClick={handleConfirmDelete}>
								Löschen
							</Button>
						</DialogActions>
					</Dialog>
					<SplitPaymentDialog
						open={splitDialogOpen}
						onClose={handleCancelSplit}
						onConfirm={handleConfirmSplit}
						totalAmount={splitTarget?.amount ?? 0}
						paymentInfo={splitTarget?.paymentInfo}
						relevantEvents={splitTarget?.relevantEvents}
					/>
				</Box>
			</CardContent>
		</Card>
	);
}
